import { isEmpty } from 'lodash';
import { AfterCellChanged, InitialChanges } from 'common/types/scalarSpreadsheet';
import type { ValuationSummaryCellCustomData } from 'pages/ValuationsAllocation/approaches/ValuationSummary/config/types';
import { EQUITY_ALLOCATION_KEY } from 'pages/ValuationsAllocation/common/constants/equityAllocation';
import { EQUITY_ALLOCATION_SPREADSHEET_SCENARIO_WEIGHTING_PROBABILITY } from 'pages/ValuationsAllocation/common/constants/equityAllocation/sheetAliases';
import { VALUATION_SUMMARY_SPREADSHEET_SCENARIO_WEIGHTING_PROBABILITY } from 'pages/ValuationsAllocation/common/constants/valuationSummary/sheetAliases';
import { adjustProbabilityValues } from 'pages/ValuationsAllocation/util';
import { getObjectValue } from 'utilities';

const customAfterCellsChanged: AfterCellChanged = (initialChanges, cells, rowConfig, tableData, spreadsheets) => {
  // Reevaluation replaces cell objects while an editor or response can retain the
  // previous build. Adjust and reverse-parse the same live cell, never a detached
  // copy or a different approach/scenario that now occupies its coordinates.
  const resolvedInitialChanges = initialChanges.flatMap(change => {
    const originalCell = change.originalCell ?? change.cell;
    const currentCell = cells[change.cell.key];
    const previousData = getObjectValue<ValuationSummaryCellCustomData>(originalCell.data);
    const currentData = getObjectValue<ValuationSummaryCellCustomData>(currentCell?.data);
    const scenarioIdsMatch =
      previousData.scenarioId && currentData.scenarioId && previousData.scenarioId === currentData.scenarioId;
    const scenarioRefsMatch =
      previousData.scenarioRef && currentData.scenarioRef && previousData.scenarioRef === currentData.scenarioRef;
    const scenarioIdsConflict =
      previousData.scenarioId && currentData.scenarioId && previousData.scenarioId !== currentData.scenarioId;
    const scenarioRefsConflict =
      previousData.scenarioRef && currentData.scenarioRef && previousData.scenarioRef !== currentData.scenarioRef;
    const hasNoScenarioIdentity =
      !previousData.scenarioId && !currentData.scenarioId && !previousData.scenarioRef && !currentData.scenarioRef;

    // Persistence can add an ID to the same scenario_ref. Keep a shared identity,
    // but reject conflicting IDs/refs or a different approach occupying this cell.
    if (
      !currentCell ||
      currentCell.sheet !== originalCell.sheet ||
      currentCell.alias !== originalCell.alias ||
      currentData.panelId !== previousData.panelId ||
      scenarioIdsConflict ||
      scenarioRefsConflict ||
      !(scenarioIdsMatch || scenarioRefsMatch || hasNoScenarioIdentity)
    ) {
      return [];
    }
    return [{ ...change, cell: currentCell }];
  });
  const changes: InitialChanges[] = [...resolvedInitialChanges];
  resolvedInitialChanges.forEach(change => {
    const { cell, value } = change;
    const { alias } = cell;

    if (alias === VALUATION_SUMMARY_SPREADSHEET_SCENARIO_WEIGHTING_PROBABILITY) {
      // Use adjustProbabilityValues to handle probability adjustments
      const probabilityChanges = adjustProbabilityValues({
        cells,
        changedCell: cell,
        newValue: value,
        probabilityCellAlias: VALUATION_SUMMARY_SPREADSHEET_SCENARIO_WEIGHTING_PROBABILITY,
        summaryCellAlias: EQUITY_ALLOCATION_SPREADSHEET_SCENARIO_WEIGHTING_PROBABILITY,
        summarySpreadsheet: spreadsheets[EQUITY_ALLOCATION_KEY],
      });

      // Add all changes to the main changes array
      // The legacy helper widens ICell, but receives these evaluated live cells.
      changes.push(...(probabilityChanges as InitialChanges[]));
    }

    // Allocation scenario columns (except the weighting cell)
    if (!isEmpty(cell.data) && cell.alias !== VALUATION_SUMMARY_SPREADSHEET_SCENARIO_WEIGHTING_PROBABILITY) {
      const probabilityChanges = adjustProbabilityValues({
        cells: Object.values(cells)
          .filter(
            c =>
              c.columnLegend === cell.columnLegend &&
              c.alias !== VALUATION_SUMMARY_SPREADSHEET_SCENARIO_WEIGHTING_PROBABILITY
          )
          .reduce<typeof cells>(
            (acc, currentCell) => ({
              ...acc,
              [String(currentCell.key)]: currentCell,
            }),
            {}
          ),
        changedCell: cell,
        newValue: value,
        probabilityCellAlias: '',
      });

      changes.push(...(probabilityChanges as InitialChanges[]));
    }
  });
  return changes;
};

export default customAfterCellsChanged;
